import Section from "@/pages/settings/components/sections/Section";
import AddActionButton from "@/components/ui/add-action-button";
import DraggableReasonListContainer from "@/pages/settings/components/shared/reasons/DraggableReasonListContainer";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import Input from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { LeadReason } from "@/entities/settings/types";

const EMPTY_IDS: number[] = [];

interface Props {
  tPrefix: string;
  droppableId: string;
  serverReasons: LeadReason[];
  refetch: () => Promise<unknown>;
  onCreate: (name: string) => Promise<unknown>;
  onEdit: (id: string, name: string) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  onReorder: (orderedIds: number[]) => Promise<unknown>;
  isCreating?: boolean;
  protectedEditIds?: number[];
  protectedEditTooltip?: string;
}

const ReasonListSection: React.FC<Props> = ({
  tPrefix,
  droppableId,
  serverReasons,
  refetch,
  onCreate,
  onEdit,
  onDelete,
  onReorder,
  isCreating = false,
  protectedEditIds = EMPTY_IDS,
  protectedEditTooltip
}) => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [newReasonValue, setNewReasonValue] = useState("");
  const [localReasons, setLocalReasons] = useState<LeadReason[]>(serverReasons);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setLocalReasons(serverReasons);
  }, [serverReasons]);

  const handleAddNewReason = async () => {
    const trimmedValue = newReasonValue.trim();
    if (!trimmedValue) return;
    setIsSubmitting(true);
    try {
      await onCreate(trimmedValue);
      setNewReasonValue("");
      await refetch().catch(() => undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (id: string, newTitle: string) => {
    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) return;

    const previousReasons = [...localReasons];
    setLocalReasons((prev) =>
      prev.map((reason) =>
        reason.id.toString() === id ? { ...reason, name: trimmedTitle } : reason
      )
    );

    try {
      await onEdit(id, trimmedTitle);
    } catch {
      setLocalReasons(previousReasons);
    }
  };

  const handleDelete = async (id: string) => {
    const previousReasons = [...localReasons];
    setLocalReasons((prev) =>
      prev.filter((reason) => reason.id.toString() !== id)
    );

    try {
      await onDelete(id);
    } catch {
      setLocalReasons(previousReasons);
    }
  };

  const handleItemsChange = async (updatedItems: LeadReason[]) => {
    const previousItems = [...localReasons];
    setLocalReasons(updatedItems);

    try {
      await onReorder(updatedItems.map((item) => item.id));
    } catch {
      setLocalReasons(previousItems);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setNewReasonValue("");
  };

  const handleSave = async () => {
    if (newReasonValue.trim()) {
      try {
        await handleAddNewReason();
      } catch {
        return;
      }
    }
    setIsEditMode(false);
    setNewReasonValue("");
  };

  return (
    <Section
      title={t(`${tPrefix}.title`)}
      subTitle={t(`${tPrefix}.subTitle`)}
      action={
        !isEditMode ? (
          <AddActionButton
            label={t(`${tPrefix}.add`)}
            onClick={() => setIsEditMode(true)}
          />
        ) : null
      }
      subTitleBreakAfterSentence
    >
      {isEditMode ? (
        <div>
          <div className="rounded-2xl border border-border-border1 bg-surface-widget-card overflow-hidden">
            <div
              className={cn(
                "w-full flex flex-col items-start justify-center",
                "max-w-[800px] pl-1 pr-2 py-4 gap-4",
                "bg-surface-widget-card",
                localReasons.length > 0 && "border-b border-border-border1"
              )}
            >
              <div className="flex flex-row w-full justify-between items-center">
                <div className="flex flex-row items-center w-full gap-0">
                  <div className="w-4 h-4 flex-shrink-0"></div>
                  <div className="flex flex-col w-full justify-start gap-1">
                    <div className="flex w-full items-stretch gap-6">
                      <Input
                        placeholder={t(`${tPrefix}.addReasonPlaceholder`)}
                        value={newReasonValue}
                        onChange={(e) => setNewReasonValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        autoFocus
                        containerClassName="flex-1"
                        className="h-8"
                        disabled={isCreating || isSubmitting}
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          onClick={handleCancel}
                          disabled={isCreating || isSubmitting}
                        >
                          {t("cancel")}
                        </Button>
                        <Button
                          onClick={handleSave}
                          disabled={isCreating || isSubmitting}
                        >
                          {isCreating ? `${t("save")}...` : t("save")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {localReasons.map((reason, index) => (
              <div
                key={reason.id}
                className={cn(
                  "w-full flex flex-col items-start justify-center",
                  "max-w-[800px] pl-1 pr-2 py-4 gap-4",
                  "bg-surface-widget-card",
                  index !== localReasons.length - 1 &&
                    "border-b border-border-border1"
                )}
              >
                <div className="flex flex-row w-full justify-between items-center">
                  <div className="flex flex-row items-center w-full gap-0">
                    <div className="w-4 h-4 flex-shrink-0"></div>
                    <div className="flex flex-col w-full justify-start gap-1">
                      <Text variant="body">{reason.name}</Text>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <DraggableReasonListContainer
          items={localReasons}
          onItemsChange={handleItemsChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
          tPrefix={tPrefix}
          droppableId={droppableId}
          protectedEditIds={protectedEditIds}
          protectedEditTooltip={protectedEditTooltip}
        />
      )}
    </Section>
  );
};

export default ReasonListSection;
